// StaffUsersTable.jsx
import React from 'react';
import { MdDelete, MdBlock, MdInfo, MdMessage, MdLockOpen } from 'react-icons/md';
import StatusTag from './StatusTag';
import { formatPhoneNumber } from '../Helpers';

const StaffUsersTable = ({
  clients,
  filteredClients,
  getLatestAppointmentStatus,
  handleDelete,
  handleBlock,
  handleUnblock,
  setSelectedClient,
  setClientForm,
  setShowClientModal,
  setChatPartner,
  setShowChatModal,
  userIsSuperstaff,
  setViewStaffUser,
  setShowStaffInfoModal,
  auth,
  starredClients,
  staffUnreadCounts = {},
  showNonAbortionMinded = false,

  // NEW: unconfirmed support
  getLatestAppointmentForPhone,
  isApptUnconfirmed,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto text-JMJGray border-collapse">
        <thead>
          <tr>
            <th className="border px-4 py-2 text-left whitespace-nowrap text-sm">Full Name</th>
            <th className="border px-4 py-2 text-left whitespace-nowrap text-sm">Phone Number</th>
            <th className="border px-4 py-2 text-left whitespace-nowrap text-sm">Appointment Status</th>
            <th className="border px-4 py-2 text-left whitespace-nowrap text-sm">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredClients
            .filter((client) => {
              if (showNonAbortionMinded) return true;
              const first = (client.firstName || "").trim();
              const last = (client.lastName || "").trim();
              // Clients who have no name set are those who did not answer no or unsure-no to the questionnaire
              return first.length > 0 || last.length > 0;
            })
            .map((client) => {
              const isBlocked = client.unableToAct === true;
              const isStaffAccount = client.id.toString().includes('STAFF');
              const isSuperstaffAccount = client.superstaff === true;
              const isSelf =
                client.id === auth.client?.id ||
                (client.phone && auth.client?.phone && client.phone.toString() === auth.client.phone.toString());
              const isStarred =
                starredClients?.has(client.phone?.toString()) &&
                !isSelf &&
                !isStaffAccount &&
                !isSuperstaffAccount;

              const unreadForThisStaff = staffUnreadCounts[client.id] || 0;

              // NEW: determine if this client has an unconfirmed upcoming/latest appointment
              const latestAppt =
                !isStaffAccount && typeof getLatestAppointmentForPhone === 'function'
                  ? getLatestAppointmentForPhone(client.phone)
                  : null;

              const hasUnconfirmed =
                !isStaffAccount &&
                latestAppt &&
                typeof isApptUnconfirmed === 'function' &&
                isApptUnconfirmed(latestAppt);

              return (
                <tr key={client.id} className={`${isSelf ? 'font-bold' : ''}`}>
                  <td className="border px-4 py-2 whitespace-nowrap text-sm">
                    {isStarred && (
                      <span title="Booked or rescheduled in the last 12 hours" className="mr-1 text-yellow-500">
                        ★
                      </span>
                    )}
                    {client.firstName} {client.lastName}{' '}
                    {client.superstaff ? '(ADMIN)' : isStaffAccount ? '(STAFF)' : ''}
                    {isStaffAccount && !isSelf && unreadForThisStaff > 0 && (
                      <span
                        className="ml-2 align-middle inline-flex items-center justify-center text-[11px] font-bold rounded-full bg-red-600 text-white min-w-[18px] h-[18px] px-2"
                        title={`${unreadForThisStaff} unread from this staff member`}
                      >
                        {unreadForThisStaff} message{unreadForThisStaff !== 1 ? 's' : ''}
                      </span>
                    )}
                  </td>

                  <td className="border px-4 py-2 whitespace-nowrap text-sm">
                    {formatPhoneNumber(client.phone)}
                    {isBlocked && (
                      <span className="ml-2 text-red-500 font-semibold">(Blocked)</span>
                    )}
                  </td>

                  <td className="border px-4 py-2 whitespace-nowrap text-sm">
                    {!isBlocked ? (
                      <div className="inline-flex items-center gap-2">
                        <StatusTag status={getLatestAppointmentStatus(client.id)} language="English" />
                        {hasUnconfirmed && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-200 text-black font-extrabold text-[11px] uppercase">
                            Unconfirmed
                          </span>
                        )}
                      </div>
                    ) : (
                      ''
                    )}
                  </td>

                  <td className="border px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // --- If blocked: show Unblock (and Info) ---
                        if (isBlocked) {
                          // For staff accounts, only superstaff can act; for clients, anyone can unblock
                          if (!isStaffAccount || (isStaffAccount && userIsSuperstaff)) {
                            return (
                              <>
                                <button
                                  onClick={() => handleUnblock(client.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-2 flex items-center rounded"
                                  title="Unblock user"
                                >
                                  <MdLockOpen className="mr-1" />
                                  Unblock
                                </button>

                                {/* Optional Info while blocked */}
                                <button
                                  onClick={() => {
                                    if (isStaffAccount) {
                                      setViewStaffUser(client);
                                      setShowStaffInfoModal(true);
                                    } else {
                                      setSelectedClient(client);
                                      setClientForm({
                                        adminNotes: client.adminNotes || '',
                                        dob: client.dob || '',
                                        email: client.email || '',
                                        firstName: client.firstName || '',
                                        language: client.language || '',
                                        lastName: client.lastName || '',
                                        menstrual: client.menstrual || '',
                                        notes: client.notes || '',
                                        phone: client.phone || '',
                                      });
                                      setShowClientModal(true);
                                    }
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center"
                                >
                                  <MdInfo className="mr-1" />
                                  Info
                                </button>
                              </>
                            );
                          }
                          return <p className="text-gray-400">No actions available</p>;
                        }

                        // --- Unblocked CLIENT accounts ---
                        if (!isStaffAccount && !isBlocked) {
                          return (
                            <>
                              <button
                                onClick={() =>
                                  handleDelete(client.id, client.phone, `${client.firstName} ${client.lastName}`)
                                }
                                className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 flex items-center rounded"
                              >
                                <MdDelete className="mr-1" />
                                Delete
                              </button>

                              <button
                                onClick={() => handleBlock(client.id, client.phone)}
                                className="bg-red-600 hover:bg-red-700 text-white flex items-center font-bold py-1 px-2 rounded"
                              >
                                <MdBlock className="mr-1" />
                                Block
                              </button>

                              <button
                                onClick={() => {
                                  setSelectedClient(client);
                                  setClientForm({
                                    adminNotes: client.adminNotes || '',
                                    dob: client.dob || '',
                                    email: client.email || '',
                                    firstName: client.firstName || '',
                                    language: client.language || '',
                                    lastName: client.lastName || '',
                                    menstrual: client.menstrual || '',
                                    notes: client.notes || '',
                                    phone: client.phone || '',
                                  });
                                  setShowClientModal(true);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center"
                              >
                                <MdInfo className="mr-1" />
                                Info
                              </button>

                              {!isSelf && (
                                <button
                                  onClick={() => {
                                    setChatPartner({
                                      id: client.id,
                                      firstName: client.firstName,
                                      lastName: client.lastName,
                                    });
                                    setShowChatModal(true);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center"
                                >
                                  <MdMessage className="mr-1" />
                                  Chat
                                </button>
                              )}
                            </>
                          );
                        }

                        // --- Unblocked STAFF accounts ---
                        if (isStaffAccount) {
                          if (!isBlocked) {
                            return (
                              <>
                                {userIsSuperstaff && !isSuperstaffAccount && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleDelete(client.id, client.phone, `${client.firstName} ${client.lastName}`)
                                      }
                                      className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 flex items-center rounded"
                                    >
                                      <MdDelete />
                                      Delete
                                    </button>
                                    <button
                                      onClick={() => {
                                        setViewStaffUser(client);
                                        setShowStaffInfoModal(true);
                                      }}
                                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center"
                                    >
                                      <MdInfo />
                                      Info
                                    </button>
                                  </>
                                )}
                                {!isSelf && (
                                  <button
                                    onClick={() => {
                                      setChatPartner({
                                        id: client.id,
                                        firstName: client.firstName,
                                        lastName: client.lastName,
                                      });
                                      setShowChatModal(true);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center"
                                  >
                                    <MdMessage className="mr-1" />
                                    Chat
                                  </button>
                                )}
                              </>
                            );
                          }
                          return <p className="text-gray-400">No actions available</p>;
                        }

                        return <p className="text-gray-400">No actions available</p>;
                      })()}
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default StaffUsersTable;
